/**
 * FirstTimeSetupModal - Shows on first login to help users set up the app properly.
 * Detects browser/device type and shows appropriate instructions.
 *
 * Two sections:
 * 1. Install as PWA (browser-specific instructions)
 * 2. Configure camera settings (browser-specific instructions)
 */
import { useState, useEffect, useMemo } from 'react';
import { Smartphone, Camera } from 'lucide-react';

const SETUP_SEEN_KEY = 'momento_setup_seen';

// Browser/Device detection
interface BrowserInfo {
  device: 'ios' | 'android' | 'desktop';
  browser: 'safari' | 'chrome' | 'brave' | 'firefox' | 'samsung' | 'edge' | 'other';
  canInstallPWA: boolean;
  needsSafariForPWA: boolean; // iOS non-Safari browsers can't install PWAs properly
}

function detectBrowser(): BrowserInfo {
  const ua = navigator.userAgent.toLowerCase();
  const vendor = navigator.vendor?.toLowerCase() || '';

  // Device detection
  const isIOS = /iphone|ipad|ipod/.test(ua);
  const isAndroid = /android/.test(ua);
  const device = isIOS ? 'ios' : isAndroid ? 'android' : 'desktop';

  // Browser detection
  let browser: BrowserInfo['browser'] = 'other';

  if (isIOS) {
    // iOS browser detection (all use WebKit but have different UIs)
    if (/crios/.test(ua)) {
      browser = 'chrome';
    } else if (/fxios/.test(ua)) {
      browser = 'firefox';
    } else if (/brave/.test(ua)) {
      browser = 'brave';
    } else if (/safari/.test(ua) && vendor.includes('apple')) {
      browser = 'safari';
    } else {
      browser = 'other';
    }
  } else if (isAndroid) {
    // Android browser detection
    if (/samsungbrowser/.test(ua)) {
      browser = 'samsung';
    } else if (/brave/.test(ua)) {
      browser = 'brave';
    } else if (/firefox/.test(ua)) {
      browser = 'firefox';
    } else if (/edg/.test(ua)) {
      browser = 'edge';
    } else if (/chrome/.test(ua)) {
      browser = 'chrome';
    } else {
      browser = 'other';
    }
  } else {
    // Desktop
    if (/safari/.test(ua) && vendor.includes('apple')) {
      browser = 'safari';
    } else if (/brave/.test(ua)) {
      browser = 'brave';
    } else if (/firefox/.test(ua)) {
      browser = 'firefox';
    } else if (/edg/.test(ua)) {
      browser = 'edge';
    } else if (/chrome/.test(ua)) {
      browser = 'chrome';
    }
  }

  // PWA capabilities
  const canInstallPWA = (isIOS && browser === 'safari') || isAndroid || device === 'desktop';
  const needsSafariForPWA = isIOS && browser !== 'safari';

  return { device, browser, canInstallPWA, needsSafariForPWA };
}

// Get browser-specific PWA install instructions
function getPWAInstructions(info: BrowserInfo): { steps: string[]; note?: string } {
  const { device, browser, needsSafariForPWA } = info;

  if (needsSafariForPWA) {
    return {
      steps: [],
      note: `To install Momento as an app, please open it in Safari. ${browser === 'chrome' ? 'Chrome' : browser === 'brave' ? 'Brave' : browser === 'firefox' ? 'Firefox' : 'This browser'} on iPhone can't install web apps to the home screen.`
    };
  }

  if (device === 'ios' && browser === 'safari') {
    return {
      steps: [
        'Tap the Share button (square with arrow) at the bottom',
        'Scroll down and tap "Add to Home Screen"',
        'Tap "Add" in the top right'
      ]
    };
  }

  if (device === 'android') {
    switch (browser) {
      case 'chrome':
        return {
          steps: [
            'Tap the menu (3 dots) in the top right',
            'Tap "Add to Home screen"',
            'Tap "Add"'
          ]
        };
      case 'samsung':
        return {
          steps: [
            'Tap the menu (3 lines) at the bottom',
            'Tap "Add page to"',
            'Tap "Home screen"'
          ]
        };
      case 'brave':
        return {
          steps: [
            'Tap the menu (3 dots) at the bottom',
            'Tap "Add to Home screen"',
            'Tap "Add"'
          ]
        };
      case 'firefox':
        return {
          steps: [
            'Tap the menu (3 dots)',
            'Tap "Install"',
            'Confirm the installation'
          ]
        };
      case 'edge':
        return {
          steps: [
            'Tap the menu (3 dots) at the bottom',
            'Tap "Add to phone"',
            'Tap "Add"'
          ]
        };
      default:
        return {
          steps: [
            'Open your browser menu',
            'Look for "Add to Home screen" or "Install"',
            'Follow the prompts to add'
          ]
        };
    }
  }

  // Desktop
  return {
    steps: [
      'Look for an install icon in the address bar',
      'Or use your browser menu to "Install app"'
    ],
    note: 'Momento works best on mobile devices!'
  };
}

// Get browser-specific camera settings instructions
function getCameraInstructions(info: BrowserInfo): { steps: string[]; autoSaved: boolean; note?: string } {
  const { device, browser } = info;

  if (device === 'ios') {
    if (browser === 'safari') {
      return {
        autoSaved: false,
        steps: [
          'Open Settings on your iPhone',
          'Scroll down and tap Safari',
          'Tap Camera (under "Settings for Websites")',
          'Select Allow',
          'Go back, tap Microphone, select Allow'
        ],
        note: 'This saves your preference permanently!'
      };
    } else {
      // Chrome, Brave, Firefox on iOS all use Safari's WebKit
      return {
        autoSaved: false,
        steps: [
          'Open Settings on your iPhone',
          `Scroll down and tap ${browser === 'chrome' ? 'Chrome' : browser === 'brave' ? 'Brave' : browser === 'firefox' ? 'Firefox' : 'your browser'}`,
          'Tap Camera and select Allow',
          'Tap Microphone and select Allow'
        ]
      };
    }
  }

  if (device === 'android') {
    // Most Android browsers save permission after first grant
    if (browser === 'brave') {
      return {
        autoSaved: true,
        steps: [
          'When prompted, tap "Allow" for camera and microphone',
          'Your choice is saved automatically!'
        ],
        note: 'Brave saves your permission after you allow once.'
      };
    }

    return {
      autoSaved: true,
      steps: [
        'When prompted, tap "Allow" for camera',
        'Tap "Allow" for microphone',
        'Your choice is saved automatically!'
      ],
      note: 'Android remembers your choice - you only need to allow once!'
    };
  }

  // Desktop
  return {
    autoSaved: true,
    steps: [
      'When prompted, click "Allow" for camera access',
      'Click "Allow" for microphone access'
    ]
  };
}

interface FirstTimeSetupModalProps {
  onComplete: () => void;
}

export default function FirstTimeSetupModal({ onComplete }: FirstTimeSetupModalProps) {
  const [step, setStep] = useState<'pwa' | 'camera' | null>(null);
  const browserInfo = useMemo(() => detectBrowser(), []);
  const pwaInstructions = useMemo(() => getPWAInstructions(browserInfo), [browserInfo]);
  const cameraInstructions = useMemo(() => getCameraInstructions(browserInfo), [browserInfo]);

  const [isStandalone] = useState(() =>
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as { standalone?: boolean }).standalone === true
  );

  useEffect(() => {
    // Skip PWA step if already installed or can't install
    if (isStandalone) {
      setStep('camera');
    } else {
      setStep('pwa');
    }
  }, [isStandalone]);

  const handlePWADone = () => {
    setStep('camera');
  };

  const handleCameraDone = () => {
    localStorage.setItem(SETUP_SEEN_KEY, 'true');
    onComplete();
  };

  const handleSkipAll = () => {
    localStorage.setItem(SETUP_SEEN_KEY, 'true');
    onComplete();
  };

  // Get friendly browser name
  const getBrowserName = () => {
    const names: Record<string, string> = {
      safari: 'Safari',
      chrome: 'Chrome',
      brave: 'Brave',
      firefox: 'Firefox',
      samsung: 'Samsung Internet',
      edge: 'Edge',
      other: 'your browser'
    };
    return names[browserInfo.browser] || 'your browser';
  };

  if (!step) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleSkipAll} />

      {/* Modal */}
      <div className="relative w-full max-w-md mx-4 mb-safe bg-background rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl animate-slide-up max-h-[85vh] flex flex-col">
        {/* Progress dots — only when both steps actually run (BUG-020).
            Installed users skip the install step and get no Back button, so a
            second dot advertised a page that wasn't there. */}
        {!isStandalone && (
          <div className="flex justify-center gap-2 pt-4 pb-2 flex-shrink-0">
            <div className={`w-2 h-2 rounded-full transition-colors ${step === 'pwa' ? 'bg-primary' : 'bg-surface-active'}`} />
            <div className={`w-2 h-2 rounded-full transition-colors ${step === 'camera' ? 'bg-primary' : 'bg-surface-active'}`} />
          </div>
        )}

        {/* Scrollable content — overscroll contained so it can't chain into
            the panels behind the sheet */}
        <div className={`flex-1 overflow-y-auto overscroll-contain px-6 pb-6 ${isStandalone ? 'pt-6' : ''}`}>
          {/* PWA Install Step */}
          {step === 'pwa' && (
            <div>
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Smartphone size={32} className="text-primary" />
                </div>
                <h2 className="text-xl font-bold text-text mb-2">Install Momento</h2>
                <p className="text-text-muted text-sm">Add to your home screen for the best experience</p>
              </div>

              {/* Browser-specific note if can't install */}
              {pwaInstructions.note && !pwaInstructions.steps.length && (
                <div className="p-4 rounded-2xl bg-warning/10 border border-warning/20 mb-4">
                  <p className="text-sm text-warning">{pwaInstructions.note}</p>
                </div>
              )}

              {/* Installation steps */}
              {pwaInstructions.steps.length > 0 && (
                <div className="p-4 rounded-2xl bg-surface">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs font-medium text-text-muted uppercase tracking-wide">
                      {getBrowserName()} on {browserInfo.device === 'ios' ? 'iPhone' : browserInfo.device === 'android' ? 'Android' : 'Desktop'}
                    </span>
                  </div>
                  <ol className="space-y-3 text-sm text-text-muted">
                    {pwaInstructions.steps.map((step, idx) => (
                      <li key={idx} className="flex gap-3">
                        <span className="w-6 h-6 rounded-full bg-primary text-on-primary flex items-center justify-center flex-shrink-0 text-xs font-bold">
                          {idx + 1}
                        </span>
                        <span dangerouslySetInnerHTML={{ __html: step.replace(/"([^"]+)"/g, '<strong class="text-text">"$1"</strong>') }} />
                      </li>
                    ))}
                  </ol>
                  {pwaInstructions.note && pwaInstructions.steps.length > 0 && (
                    <p className="text-xs text-text-muted mt-3">{pwaInstructions.note}</p>
                  )}
                </div>
              )}

              <div className="flex gap-3 mt-6">
                <button
                  onClick={handlePWADone}
                  className="flex-1 py-3 rounded-xl bg-surface text-text font-medium"
                >
                  Skip
                </button>
                <button
                  onClick={handlePWADone}
                  className="flex-1 py-3 rounded-xl bg-primary text-on-primary font-medium"
                >
                  {pwaInstructions.steps.length > 0 ? 'Done, Next' : 'Next'}
                </button>
              </div>
            </div>
          )}

          {/* Camera Settings Step */}
          {step === 'camera' && (
            <div>
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Camera size={32} className="text-primary" />
                </div>
                <h2 className="text-xl font-bold text-text mb-2">Camera Setup</h2>
                <p className="text-text-muted text-sm">
                  {cameraInstructions.autoSaved
                    ? 'Just tap Allow when prompted - easy!'
                    : 'One-time setup to avoid repeated prompts'}
                </p>
              </div>

              {/* Success message for auto-saved browsers */}
              {cameraInstructions.autoSaved && (
                <div className="p-4 rounded-2xl bg-success/10 border border-success/20 mb-4">
                  <p className="text-sm text-success font-medium">
                    {cameraInstructions.note || `${getBrowserName()} saves your permission automatically!`}
                  </p>
                </div>
              )}

              {/* Manual setup instructions for iOS Safari */}
              {!cameraInstructions.autoSaved && (
                <div className="space-y-4">
                  <div className="p-4 rounded-2xl bg-primary/5 border border-primary/20">
                    <p className="text-sm text-primary font-medium">
                      Do this once and never see camera prompts again!
                    </p>
                  </div>

                  <div className="p-4 rounded-2xl bg-surface">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs font-medium text-text-muted uppercase tracking-wide">
                        {getBrowserName()} Settings
                      </span>
                    </div>
                    <ol className="space-y-3 text-sm text-text-muted">
                      {cameraInstructions.steps.map((step, idx) => (
                        <li key={idx} className="flex gap-3">
                          <span className="w-6 h-6 rounded-full bg-primary text-on-primary flex items-center justify-center flex-shrink-0 text-xs font-bold">
                            {idx + 1}
                          </span>
                          <span dangerouslySetInnerHTML={{ __html: step.replace(/"([^"]+)"/g, '<strong class="text-text">"$1"</strong>').replace(/Settings|Safari|Camera|Microphone|Allow|Chrome|Brave|Firefox/g, '<strong class="text-text">$&</strong>') }} />
                        </li>
                      ))}
                    </ol>
                    {cameraInstructions.note && (
                      <p className="text-xs text-success mt-3">{cameraInstructions.note}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Simple steps for auto-saved */}
              {cameraInstructions.autoSaved && (
                <div className="p-4 rounded-2xl bg-surface">
                  <ol className="space-y-3 text-sm text-text-muted">
                    {cameraInstructions.steps.map((step, idx) => (
                      <li key={idx} className="flex gap-3">
                        <span className="w-6 h-6 rounded-full bg-primary text-on-primary flex items-center justify-center flex-shrink-0 text-xs font-bold">
                          {idx + 1}
                        </span>
                        <span dangerouslySetInnerHTML={{ __html: step.replace(/"([^"]+)"/g, '<strong class="text-text">"$1"</strong>').replace(/Allow/g, '<strong class="text-text">$&</strong>') }} />
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              <div className="flex gap-3 mt-6">
                {!isStandalone && (
                  <button
                    onClick={() => setStep('pwa')}
                    className="flex-1 py-3 rounded-xl bg-surface text-text font-medium"
                  >
                    Back
                  </button>
                )}
                <button
                  onClick={handleCameraDone}
                  className={`py-3 rounded-xl bg-primary text-on-primary font-medium ${isStandalone ? 'w-full' : 'flex-1'}`}
                >
                  Got It!
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Helper to check if user has seen setup
export function hasSeenSetup(): boolean {
  return localStorage.getItem(SETUP_SEEN_KEY) === 'true';
}

// Helper to reset setup (for testing)
export function resetSetup(): void {
  localStorage.removeItem(SETUP_SEEN_KEY);
}
